import { useEffect, useMemo, useRef, useState } from 'react';
import { worker } from '../../../../app.worker';
import WebWorker from 'src/WebWorker';
import { ChartItemType, MainObjectType, TimestampsTypes } from './types';
import { SelectedInputType } from '../../types/selected-input';

type Props = {
  timestamps: TimestampsTypes;
  selectedTags: SelectedInputType[];
  startDate: string;
  endDate: string;
  aggregationTypePayload: 'two_hour' | 'five_min' | 'thirty_min' | null;
  isOnChange?: boolean;
};

export const useChartData = ({
  timestamps,
  selectedTags,
  startDate,
  endDate,
  aggregationTypePayload,
  isOnChange = false,
}: Props) => {
  const [data, setData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // const isOnChangeSum = isOnChange; //Define logic for descrete data
  const prevDepsRef = useRef({ timestamps, selectedTags, aggregationTypePayload });
  if (
    prevDepsRef.current.timestamps !== timestamps ||
    prevDepsRef.current.selectedTags !== selectedTags ||
    prevDepsRef.current.aggregationTypePayload !== aggregationTypePayload
  ) {
    prevDepsRef.current = { timestamps, selectedTags, aggregationTypePayload };
    if (selectedTags && selectedTags.length > 0 && !isLoading) {
      setIsLoading(true);
    }
  }

  useEffect(() => {
    if (!selectedTags || selectedTags.length === 0) return;

    setIsLoading(true);
    let completedRequests = 0;
    const collectedData: any[] = [];

    const webWorker = new WebWorker(worker);

    webWorker.onmessage = (e: MessageEvent) => {
      const { status, error } = e.data;

      if (status === 'success') {
        collectedData.push(e.data.response);
      } else {
        console.error(error);
      }
      completedRequests++;
      if (completedRequests === selectedTags.length) {
        setData(collectedData);
        setIsLoading(false);
      }
    };

    selectedTags?.forEach((tag) => {
      if (tag?.gatewayId) {
        webWorker.postMessage({
          gatewayId: tag?.gatewayId,
          startDate: timestamps.fromDate ? startDate : null,
          endDate: timestamps.toDate ? endDate : null,
          measurements: tag?.inputId,
          aggregationType: tag.aggregation === 'Auto' ? aggregationTypePayload : tag.aggregation,
          apiUrl: import.meta.env.VITE_STREAMETRIC_API,
          isOnChange: tag.statistic === 'On-change' ? true : false,
          statistics:
            tag.statistic === 'Auto' || tag.statistic === 'On-change' ? null : tag.statistic,
        });
      }
    });

    return () => {
      webWorker.terminate();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timestamps, selectedTags, aggregationTypePayload]);

  const formatedNewData = useMemo(
    () =>
      data.map((item: ChartItemType) => {
        const dataObj = Object.entries(item?.data);
        const dataEntry = dataObj[0];
        const tagId = dataEntry[0];
        const valuesObj: MainObjectType = dataEntry[1];

        const matchedTag = selectedTags.find((tag) => tag.inputId === tagId);
        const statistic = matchedTag?.statistic;
        const values =
          statistic && statistic !== 'Auto' && valuesObj.statistics?.[statistic]
            ? valuesObj.statistics[statistic]
            : valuesObj.values;

        return { tagId, values };
      }),
    [data, selectedTags]
  );

  return { data: formatedNewData, isLoading };
};
